import * as THREE from 'three';
import { GLTFLoader, type GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import type { ChapterId } from './types';

const loader = new GLTFLoader();
const cache = new Map<string, Promise<GLTF>>();

function load(url: string): Promise<GLTF> {
  let pending = cache.get(url);
  if (!pending) {
    pending = loader.loadAsync(url);
    cache.set(url, pending);
  }
  return pending;
}

interface Placement {
  model: string;
  position: readonly [number, number, number];
  rotationY?: number;
  size: number;
  scale?: readonly [number, number, number];
}

const SETS: Record<ChapterId, readonly Placement[]> = {
  veil: [
    { model: 'Wall_Arch', position: [0, 0, -8.5], rotationY: Math.PI, size: 7.2 },
    { model: 'Wall_Plaster_Straight', position: [-6.4, 0, -10], size: 7.5 },
    { model: 'Wall_Plaster_Straight', position: [6.4, 0, -10], size: 7.5 },
  ],
  'bread-and-night': [
    { model: 'Floor_Brick', position: [0, 0.015, -0.8], size: 11, scale: [1.25, 1, 1.15] },
    { model: 'Wall_Plaster_Door_Round', position: [0, 0, -4.7], size: 9.5 },
    { model: 'Wall_Plaster_Straight', position: [-6.2, 0, -4.7], size: 8.8 },
    { model: 'Wall_Plaster_Straight', position: [6.2, 0, -4.7], size: 8.8 },
    { model: 'Prop_Crate', position: [-4.6, 0, -2.9], rotationY: 0.35, size: 1.15 },
  ],
  'garden-and-judgment': [
    { model: 'Wall_Arch', position: [0, 0, -7.7], rotationY: Math.PI, size: 7 },
    { model: 'Stairs_Exterior_Straight', position: [0, 0, -5.8], rotationY: Math.PI, size: 4.6 },
    { model: 'Wall_UnevenBrick_Straight', position: [-7.1, 0, -7.9], size: 8 },
    { model: 'Wall_UnevenBrick_Straight', position: [7.1, 0, -7.9], size: 8 },
    { model: 'Prop_Crate', position: [-4.1, 0, 0.4], rotationY: -0.4, size: 1.1 },
  ],
  golgotha: [
    { model: 'Prop_Wagon', position: [5.5, 0, 1.8], rotationY: -0.75, size: 3.8 },
    { model: 'Wall_UnevenBrick_Straight', position: [-8.4, 0, -7.5], rotationY: 0.2, size: 8.5 },
    { model: 'Wall_Arch', position: [8.5, 0, -9], rotationY: -0.3, size: 7.3 },
  ],
  silence: [
    { model: 'Floor_Brick', position: [0, 0.012, -1], size: 10, scale: [1.3, 1, 1.3] },
    { model: 'DoorFrame_Round_Brick', position: [0, 0, -3.5], rotationY: Math.PI, size: 5.2 },
    { model: 'Wall_UnevenBrick_Straight', position: [-5.2, 0, -4.2], size: 7 },
    { model: 'Wall_UnevenBrick_Straight', position: [5.2, 0, -4.2], size: 7 },
  ],
  'garden-reopened': [
    { model: 'DoorFrame_Round_Brick', position: [0, 0, -3.4], rotationY: Math.PI, size: 5.2 },
    { model: 'Wall_Arch', position: [0, 0, -4.05], rotationY: Math.PI, size: 6.2 },
    { model: 'Wall_UnevenBrick_Straight', position: [-5.3, 0, -4.4], size: 7 },
    { model: 'Wall_UnevenBrick_Straight', position: [5.3, 0, -4.4], size: 7 },
    { model: 'Prop_Crate', position: [4.5, 0, 0.8], rotationY: 0.5, size: 0.9 },
  ],
  'eyes-opened': [
    { model: 'Floor_Brick', position: [0, 0.012, -1.8], size: 12, scale: [1.25, 1, 1.25] },
    { model: 'Wall_Arch', position: [0, 0, -8.8], rotationY: Math.PI, size: 8 },
    { model: 'Wall_Plaster_Straight', position: [-7.5, 0, -9.1], size: 8 },
    { model: 'Wall_Plaster_Straight', position: [7.5, 0, -9.1], size: 8 },
    { model: 'Prop_Wagon', position: [6.4, 0, -2.5], rotationY: -0.5, size: 3.4 },
  ],
};

async function instantiatePlacement(placement: Placement): Promise<THREE.Group> {
  const gltf = await load(`/assets/3d/medieval/${placement.model}.gltf`);
  const object = gltf.scene.clone(true);
  object.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(object);
  const size = box.getSize(new THREE.Vector3());
  const longest = Math.max(size.x, size.y, size.z, 0.001);
  const normalized = placement.size / longest;
  const center = box.getCenter(new THREE.Vector3());
  object.scale.setScalar(normalized);
  object.position.set(-center.x * normalized, -box.min.y * normalized, -center.z * normalized);
  object.rotation.y = placement.rotationY ?? 0;
  if (placement.scale) object.scale.multiply(new THREE.Vector3(...placement.scale));
  object.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.castShadow = true;
      child.receiveShadow = true;
      child.frustumCulled = true;
    }
  });

  const holder = new THREE.Group();
  holder.name = `production-set:${placement.model}`;
  holder.position.set(...placement.position);
  holder.add(object);
  return holder;
}

export async function createChapterAssetSet(chapter: ChapterId): Promise<THREE.Group> {
  const root = new THREE.Group();
  root.name = `production-set:${chapter}`;
  const models = await Promise.all(SETS[chapter].map(instantiatePlacement));
  root.add(...models);
  return root;
}
